import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert, Tabs, Space, Typography, Tag, Select } from 'antd';
import { useAuth } from '@/contexts/AuthContext';
import { TournamentService } from '@/services/tournament.service';
import { Match } from '@/types/api.types';
import { BackToHome } from '@/components/common/BackToHome';
import { TournamentFiles } from './TournamentFiles';
import { logger } from '@/utils/logger';
import {
  BracketContainer,
  Round,
  MatchBox,
  Participant,
  MatchInfo,
  CategorySelector
} from './styles/TournamentSchedule.styles';

const { Title } = Typography;
const { TabPane } = Tabs;

interface CategoryMatches {
    [category: string]: Match[];
}

// Добавляем интерфейс для расширенного матча с полями score1 и score2
interface ExtendedMatch extends Match {
    score1?: number;
    score2?: number;
}

const getStatusDisplay = (status: Match['status']) => {
    switch (status) {
        case 'completed':
            return { text: 'Завершён', color: 'success' };
        case 'in_progress':
            return { text: 'В процессе', color: 'processing' };
        case 'pending':
            return { text: 'Ожидается', color: 'default' };
        case 'cancelled':
            return { text: 'Отменён', color: 'error' };
        default:
            return { text: 'Ожидается', color: 'default' };
    }
};

export const TournamentSchedule: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [categoryMatches, setCategoryMatches] = useState<CategoryMatches>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [categories, setCategories] = useState<string[]>([]);

    const fetchSchedule = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await TournamentService.getTournamentSchedule(Number(id));

            if (response.data && Array.isArray(response.data)) {
                const matchesByCategory: CategoryMatches = {};
                const uniqueCategories = new Set<string>();

                response.data.forEach((match: Match) => {
                    // Преобразуем категорию в строку для использования в качестве ключа
                    const categoryKey = match.category ? `category-${match.category.id}` : 'Без категории';
                    const categoryName = match.category ? match.category.name : 'Без категории';

                    uniqueCategories.add(categoryName);

                    if (!matchesByCategory[categoryKey]) {
                        matchesByCategory[categoryKey] = [];
                    }
                    matchesByCategory[categoryKey].push(match);
                });

                setCategoryMatches(matchesByCategory);
                setCategories(Array.from(uniqueCategories));
            }
        } catch (error: any) {
            logger.error('Error fetching schedule:', error);
            setError(error.message || 'Ошибка при загрузке расписания');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchedule();
    }, [id]);

    const renderMatch = (match: Match) => (
        <MatchBox key={match.id} status={match.status}>
            <Participant isWinner={match.winnerParticipantId === match.participant1Id}>
                {typeof match.participant1 === 'string' ? match.participant1 :
                 match.participant1 ? `${match.participant1.firstName} ${match.participant1.lastName}` : 'TBD'}
                {match.status === 'completed' && (match as ExtendedMatch).score1 !== undefined && ` (${(match as ExtendedMatch).score1})`}
            </Participant>
            <Participant isWinner={match.winnerParticipantId === match.participant2Id}>
                {typeof match.participant2 === 'string' ? match.participant2 :
                 match.participant2 ? `${match.participant2.firstName} ${match.participant2.lastName}` : 'TBD'}
                {match.status === 'completed' && (match as ExtendedMatch).score2 !== undefined && ` (${(match as ExtendedMatch).score2})`}
            </Participant>
            <MatchInfo>
                <span>Татами {match.tatamiNumber || 'не указано'}</span>
                <Tag color={getStatusDisplay(match.status).color}>
                    {getStatusDisplay(match.status).text}
                </Tag>
            </MatchInfo>
        </MatchBox>
    );

    const renderBracket = (matches: Match[]) => {
        const roundsMap = matches.reduce((acc, match) => {
            if (!acc[match.round]) {
                acc[match.round] = [];
            }
            acc[match.round].push(match);
            return acc;
        }, {} as { [round: number]: Match[] });

        const rounds = Object.entries(roundsMap)
            .sort(([a], [b]) => Number(a) - Number(b));

        return (
            <BracketContainer>
                {rounds.map(([round, roundMatches]) => (
                    <Round key={round}>
                        <Title level={5}>Раунд {round}</Title>
                        {roundMatches
                            .sort((a, b) => (a.position || 0) - (b.position || 0))
                            .map(match => renderMatch(match))}
                    </Round>
                ))}
            </BracketContainer>
        );
    };

    if (loading) {
        return <Card loading />;
    }

    if (error) {
        return (
            <Card>
                <Alert
                    message="Ошибка"
                    description={error}
                    type="error"
                    showIcon
                />
            </Card>
        );
    }

    return (
        <>
            <BackToHome />
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Title level={2}>Турнирная сетка</Title>

                    <CategorySelector>
                        <Space>
                            <span>Категория:</span>
                            <Select
                                value={selectedCategory}
                                onChange={setSelectedCategory}
                                style={{ minWidth: 200, maxWidth: '100%', width: 'auto' }}
                                dropdownStyle={{ maxWidth: 600, minWidth: 300 }}
                                optionLabelProp="label"
                                popupMatchSelectWidth={false}
                            >
                                <Select.Option value="all" label="Все категории">Все категории</Select.Option>
                                {categories.map(category => (
                                    <Select.Option key={category} value={category} label={category} title={category}>
                                        <div style={{ 
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'normal',
                                            lineHeight: '1.5',
                                            padding: '4px 0'
                                        }}>
                                            {category}
                                        </div>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Space>
                    </CategorySelector>

                    <Tabs defaultActiveKey="schedule">
                        <TabPane tab="Расписание" key="schedule">
                            {selectedCategory === 'all' ? (
                                Object.entries(categoryMatches).map(([category, matches]) => (
                                    <div key={category}>
                                        <Title level={4}>{category}</Title>
                                        {renderBracket(matches)}
                                    </div>
                                ))
                            ) : (
                                categoryMatches[selectedCategory] &&
                                renderBracket(categoryMatches[selectedCategory])
                            )}
                        </TabPane>
                        <TabPane tab="Файлы" key="files">
                            <TournamentFiles
                                tournamentId={Number(id)}
                                canEdit={user?.role === 'admin'}
                            />
                        </TabPane>
                    </Tabs>
                </Space>
            </Card>
        </>
    );
};